import {Button, TextInput, Tooltip} from "@mantine/core";
import { Copy as IconCopy, Info as IconInfoCircle } from "@phosphor-icons/react";
import {useContext} from "react";
import MessageContext from "../../../context/MessageContext.tsx";
import {Faction} from "../../../types/general.types.ts";
import useCanUseClipboard from "../../../hooks/useCanUseClipboard.ts";



interface ContractParserProps {
    onData: (data: ContractParsedData) => void,
    networks: Faction[],
    disabled: boolean,
}

interface ContractParsedData {
    name?: string;
    type?: string;
    expiration_y?: string;
    expiration_d?: string;
    difficulty?: string;
    reward?: string;
    network?: string;
}
export default function ContractParser({onData, networks, disabled}: ContractParserProps) {
    const {success, error} = useContext(MessageContext);
    const {hasPermission, hasClipboard} = useCanUseClipboard();


    if(disabled) {
        return (
            <>
                <Tooltip label="This is a premium feature">
                    <Button leftSection={<IconCopy size={16}/>} disabled={true}>Load from clipboard</Button>
                </Tooltip>
            </>
        )
    }

    const process = (text: string) => {
        if (disabled) {
            return false;
        }
        let update = parseData(text, networks);
        let count = Object.keys(update).length;
        if (count === 0) {
            error('No data could be recognized');
        } else if (count < 7) {
            success('Partial recognition', 'Only some data could be imported, please make sure to copy the whole contract table');
        } else {
            success('Data recognized', 'All data has been imported successfully');
        }
        onData(update);
    }

    if(hasClipboard) {
        return (
            <>
                <Tooltip label={<>
                    Insert your puck into the tracking fob and select "Access Target Information"<br/>
                    Select all the information that is displayed in the interaction area and copy it<br/>
                    Come back to this page and click the "Load from clipboard button"
                </>} position="top">
                    <div className="mb-3 text-sm text-500"><IconInfoCircle size={14} style={{verticalAlign: 'middle'}}/> How to use this?</div>
                </Tooltip>
                {!hasPermission && <div><i className="text-red">Need to give clipboard access</i></div>}
                <Button leftSection={<IconCopy size={16}/>} onClick={() => {
                    navigator.clipboard.readText().then(text => process(text)).catch(err => {
                        error('Load failed', err.message)
                    })
                }} disabled={disabled || !hasPermission}>Load from clipboard</Button>
            </>
        )
    } else {
        return (
            <>
                <Tooltip label={<>
                    Insert your puck into the tracking fob and select "Access Target Information"<br/>
                    Select all the information that is displayed in the interaction area and copy it<br/>
                    Come back to this page and paste it in the input below<br />
                </>} position="top">
                    <div className="mb-3 text-sm text-500"><IconInfoCircle size={14} style={{verticalAlign: 'middle'}}/> How to use this?</div>
                </Tooltip>
                <TextInput value="" placeholder="Paste contract data" onChange={e => process(e.target.value)} disabled={disabled} />
            </>
        )
    }
}


const parseData = (text: string, networks: Faction[]) => {
    let update: ContractParsedData = {};

    let n = text.match(/\n?([\w\-`' ]+)\s(Kill|Rescue)/);
    if(n) {
        update.name = n[1];
    }
    let t = text.match(/\s(Kill|Rescue)/);
    if(t) {
        update.type = t[1];
    }

    let exp = text.match(/Deadline: Y(\d+)D(\d+)/)
    if(exp) {
        update.expiration_y = exp[1];
        update.expiration_d = exp[2];
    }
    let d = text.match(/Difficulty: (\w+)/);
    if(d) {
        update.difficulty = d[1];
    }
    let r = text.match(/Reward: (\w+)/);
    if(r) {
        update.reward = r[1] === 'Datacard' ? 'DC' : r[1];
    }

    let net = text.match(/\n([\w\s]+)\tCompleted/);
    if(net) {
        let network = networks.find(el => el.name === net[1]);
        if(network) {
            update.network = network.id;
        }
    }

    return update;
}
